// ============================================================================
// ProgressRing — the animated SVG ring at the heart of the fancy client
// dashboard (Wave 4). A circular gauge whose coloured stroke sweeps from 0 to
// the logged/target ratio while the big centre number counts up to the logged
// value. Reduced-motion safe: under prefers-reduced-motion it renders the final
// state with NO animation (the ring is drawn full, the number is the real value
// from first paint).
// ----------------------------------------------------------------------------
// • SVG stroke-dashoffset for the fill arc (a true ring, not a div bar).
// • Framer Motion drives BOTH the arc sweep (animate stroke-dashoffset) and the
//   count-up tween (animate 0→value, written into a text node) on one timeline.
// • Accessibility: the wrapper is a role="img" with an aria-label that always
//   states the real "<logged> of <target> <unit>" — a screenshot, a glance, or
//   a screen reader sees the true figures even before any animation. The moving
//   number text node is aria-hidden (a visual flourish).
// • Pulse colour tokens only (coral/violet/amber/lime/ocean) — never restyles a
//   locked primitive; the ring is its own SVG.
// All user-facing copy is passed in already-localized (the caller uses i18n).
// ============================================================================
import { useEffect, useRef } from "react";
import { animate, useReducedMotion } from "framer-motion";
import { cn } from "../../lib/cn";

/** The Pulse pulse-colour tokens a ring can wear (spec §1 ring palette). */
export type RingColour = "coral" | "violet" | "amber" | "lime" | "ocean";

/** Resolved stroke + soft-track + centred-number colours per Pulse token. */
const RING: Record<RingColour, { stroke: string; track: string; num: string }> = {
  // kcal — coral (the brand signal)
  coral: { stroke: "#FF5E62", track: "#FFE3E1", num: "text-volt-600" },
  // protein — violet
  violet: { stroke: "#7B5BFF", track: "#EBE5FF", num: "text-ember-600" },
  // carbs — amber
  amber: { stroke: "#F5A524", track: "#FCEBCC", num: "text-amber-600" },
  // fat — lime
  lime: { stroke: "#14B85C", track: "#D6F5E3", num: "text-lime-600" },
  // water — ocean
  ocean: { stroke: "#2D8FFF", track: "#DCEBFF", num: "text-ocean-600" },
};

export interface ProgressRingProps {
  /** Today's logged amount (the count-up target + the ring's numerator). */
  value: number;
  /**
   * The daily target (the ring's denominator). When null/0 the macro target is
   * not yet known — the ring shows a gentle near-empty arc and the centre reads
   * the logged value only (no "of N"), and the caption falls back to logged-only.
   */
  target: number | null;
  /** Localized metric label, e.g. "Calories" / "熱量". */
  label: string;
  /** Localized unit, e.g. "kcal" / "g" / "ml". */
  unit: string;
  /** Pulse pulse-colour token for the stroke (kcal coral, water ocean, …). */
  colour: RingColour;
  /** Outer diameter in px. The hero uses ~168 for kcal, ~96 for the macros. */
  size?: number;
  /** Stroke thickness in px (scales with size by default). */
  stroke?: number;
  /** Decimal places for the centre number (macros 0, fine values 0 too). */
  decimals?: number;
  /** Render the big centre number (off for tiny rings where space is tight). */
  showValue?: boolean;
  /** Stagger the entrance: seconds to wait before the sweep/count-up start. */
  delay?: number;
  /** Localized "{{logged}} of {{target}} {{unit}}" for the aria-label. */
  ariaValueText?: string;
  className?: string;
}

const CUBIC = [0.22, 0.61, 0.36, 1] as const; // Pulse `ease-kinetic`

/** Clamp a ratio to [0, 1]; null/zero target → 0 (nothing to fill against). */
function ratioOf(value: number, target: number | null): number {
  if (!target || target <= 0) return 0;
  return Math.max(0, Math.min(1, value / target));
}

export function ProgressRing({
  value,
  target,
  label,
  unit,
  colour,
  size = 96,
  stroke,
  decimals = 0,
  showValue = true,
  delay = 0,
  ariaValueText,
  className,
}: ProgressRingProps) {
  const reduce = useReducedMotion();
  const arcRef = useRef<SVGCircleElement>(null);
  const numRef = useRef<HTMLSpanElement>(null);
  const startedRef = useRef(false);

  const c = RING[colour];
  const sw = stroke ?? Math.max(6, Math.round(size * 0.1));
  const r = (size - sw) / 2;
  const circumference = 2 * Math.PI * r;
  const ratio = ratioOf(value, target);
  // dashoffset: full circumference = empty; 0 = full. We animate toward the
  // ratio so a partial day fills part-way and a hit/over-target fills the ring.
  const finalOffset = circumference * (1 - ratio);

  const fmt = (n: number) =>
    n.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  const finalText = fmt(value);

  useEffect(() => {
    const arc = arcRef.current;
    const num = numRef.current;

    // Reduced motion (or already played): snap straight to the final state.
    if (reduce) {
      if (arc) arc.style.strokeDashoffset = String(finalOffset);
      if (num) num.textContent = finalText;
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    const stops: Array<() => void> = [];

    // Sweep the arc from empty (full offset) to the ratio.
    if (arc) {
      arc.style.strokeDashoffset = String(circumference);
      const ctrl = animate(circumference, finalOffset, {
        duration: 1.1,
        delay,
        ease: CUBIC,
        onUpdate: (latest) => {
          arc.style.strokeDashoffset = String(latest);
        },
      });
      stops.push(() => ctrl.stop());
    }

    // Count the centre number up from 0 to the logged value on the same beat.
    if (num) {
      num.textContent = fmt(0);
      const ctrl = animate(0, value, {
        duration: 1.1,
        delay,
        ease: CUBIC,
        onUpdate: (latest) => {
          num.textContent = fmt(latest);
        },
        onComplete: () => {
          num.textContent = finalText; // exact final, no float drift
        },
      });
      stops.push(() => ctrl.stop());
    }

    return () => stops.forEach((s) => s());
    // finalText/finalOffset derive from value/target/decimals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce, value, target, decimals, delay]);

  // The label sits below the ring; the aria-label carries the real figures.
  const pct = Math.round(ratio * 100);

  return (
    <div
      className={cn("flex flex-col items-center gap-1.5", className)}
      role="img"
      aria-label={`${label}: ${ariaValueText ?? `${finalText} ${unit}`}${
        target ? ` (${pct}%)` : ""
      }`}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-hidden="true"
          className="-rotate-90"
        >
          {/* Soft track */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={c.track}
            strokeWidth={sw}
          />
          {/* Coloured progress arc — rounded caps for the Pulse feel. */}
          <circle
            ref={arcRef}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={c.stroke}
            strokeWidth={sw}
            strokeLinecap="round"
            strokeDasharray={circumference}
            // Default offset = the FINAL state, so a non-animating first paint
            // (or reduced motion before the effect runs) shows the real ring.
            strokeDashoffset={finalOffset}
            style={{ transition: "none" }}
          />
        </svg>

        {/* Centre figure — the count-up value (visual) + a tiny unit. */}
        {showValue && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="flex flex-col items-center leading-none">
              <span
                ref={numRef}
                aria-hidden="true"
                className={cn(
                  "nums font-display font-extrabold tracking-tightest",
                  c.num,
                )}
                style={{ fontSize: Math.max(13, Math.round(size * 0.22)) }}
              >
                {finalText}
              </span>
              <span
                aria-hidden="true"
                className="mt-0.5 font-display text-[0.6rem] font-bold uppercase tracking-kicker text-body-faint"
              >
                {unit}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Metric label + the logged/target caption (always the real figures). */}
      <div className="text-center">
        <p className="font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {label}
        </p>
        <p className="nums mt-0.5 text-xs tabular-nums text-body-muted">
          {ariaValueText ?? `${finalText} ${unit}`}
        </p>
      </div>
    </div>
  );
}
